import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import useMediaQuery from "@mui/material/useMediaQuery";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

import { isImpersonating } from "../../../helpers/impersonation";

const HOLD_INBOX_HREF =
  "/inbox?compose=account-hold&subject=Account%20Hold";

/**
 * Persistent seller-facing banner when their account has ordersOnHold.
 * While impersonating, VerticalLayout shows AdminHoldBanner instead.
 */
const SellerHoldBanner = () => {
  const user = useSelector((state) => state?.currentUser?.user);
  const isMobile = useMediaQuery("(max-width:600px)");
  const isMockUser = isImpersonating();

  const visible = !!user?.ordersOnHold && !isMockUser;

  // Clear the fixed topbar (60px). page-content margin is zeroed via body class
  // so we don't stack a second 60px gap under the banner.
  useEffect(() => {
    if (!visible) return undefined;
    document.body.classList.add("has-riverr-global-banner");
    return () => document.body.classList.remove("has-riverr-global-banner");
  }, [visible]);

  if (!visible) return null;

  if (isMobile) {
    return (
      <Alert
        severity="warning"
        icon={<WarningAmberRoundedIcon fontSize="inherit" />}
        data-riverr-global-banner
        sx={{
          mt: "60px",
          borderRadius: 0,
          alignItems: "flex-start",
          "& .MuiAlert-message": { width: "100%", p: 0 },
        }}
      >
        <AlertTitle sx={{ fontWeight: 700, mb: 0.5, fontSize: 13 }}>
          Account on hold
        </AlertTitle>
        <div style={{ fontSize: 12.5, lineHeight: 1.45 }}>
          Orders are coming in but won&apos;t enter production until this is
          released.
        </div>
        <Button
          size="small"
          component={Link}
          to={HOLD_INBOX_HREF}
          sx={{
            mt: 1,
            width: "100%",
            fontWeight: 700,
            color: "inherit",
            borderTop: "1px solid rgba(0,0,0,0.12)",
            borderRadius: 0,
            pt: 1,
          }}
        >
          Contact us immediately
        </Button>
      </Alert>
    );
  }

  return (
    <Alert
      severity="warning"
      icon={<WarningAmberRoundedIcon fontSize="inherit" />}
      data-riverr-global-banner
      sx={{ mt: "60px", borderRadius: 0, alignItems: "flex-start" }}
    >
      <AlertTitle sx={{ fontWeight: 700 }}>
        Your account has been placed on hold
      </AlertTitle>
      Orders will keep coming in, but won&apos;t move into production until the
      hold is released.{" "}
      <Link
        to={HOLD_INBOX_HREF}
        style={{ fontWeight: 600, color: "inherit" }}
      >
        Contact us immediately
      </Link>{" "}
      to message us about your Account Hold.
    </Alert>
  );
};

export default SellerHoldBanner;
